import { cn } from '@ophelos/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import * as React from 'react';

const verticalButtonVariants = cva(
  'flex flex-col items-center justify-center gap-2 rounded-xl cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-ring/50 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-50 transition-[color,box-shadow]',
  {
    variants: {
      variant: {
        default:
          'hover:bg-muted/25 hover:ring hover:ring-border-heavy data-[state=open]:bg-muted/25 data-[state=open]:ring data-[state=open]:ring-border-heavy',
        ghost: 'hover:bg-accent/50 ',
      },
      size: {
        default: 'p-4 max-w-[100px] w-full',
        sm: 'p-3 max-w-[80px] w-full',
        lg: 'p-5 max-w-[120px] w-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface VerticalButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof verticalButtonVariants> {
  icon: LucideIcon;
  label: string;
  iconClassName?: string;
  labelClassName?: string;
}

export const VerticalButton = React.forwardRef<HTMLButtonElement, VerticalButtonProps>(
  ({ className, variant, size, icon: Icon, label, iconClassName, labelClassName, ...props }, ref) => {
    return (
      <button ref={ref} className={cn(verticalButtonVariants({ variant, size, className }))} {...props}>
        <div className={cn('size-10 bg-muted rounded-full flex items-center justify-center shrink-0', iconClassName)}>
          <Icon className="size-5 text-muted-foreground" />
        </div>
        <span className={cn('text-sm text-center', labelClassName)}>{label}</span>
      </button>
    );
  }
);

VerticalButton.displayName = 'VerticalButton';
